import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BulkProcessorSdkService } from '@vendasta/bulk-actions';
import { UserListSdkService } from '@vendasta/contacts';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, expand, map, reduce, switchMap } from 'rxjs/operators';
import { PartnerService } from '../../../core/partner.service';

@Injectable({ providedIn: 'root' })
export class BulkUserImportService {
  file: File;
  fileLocation: string;

  private readonly csv: string = 'text/csv';

  constructor(
    private snackbarService: SnackbarService,
    private bulkProcessor: BulkProcessorSdkService,
    private http: HttpClient,
    private partnerService: PartnerService,
    private userListSdkService: UserListSdkService,
  ) {}

  uploadCsv(fileContent: string): Observable<void> {
    return this.bulkProcessor.generateUploadSignedUrl(this.file.name, this.csv).pipe(
      switchMap((signedUrl) => {
        return this.http
          .put(signedUrl, fileContent, {
            headers: { 'Content-Type': this.csv },
          })
          .pipe(
            map(() => this.setFileLocation(signedUrl)),
            catchError((err) => throwError(err)),
          );
      }),
    );
  }

  setFileLocation(signedUrl: string): void {
    const uuid = signedUrl.match('\\b[0-9a-f]{8}\\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\\b[0-9a-f]{12}\\b');
    if (uuid !== null) {
      this.fileLocation = uuid[0] + '/' + this.file.name;
    } else {
      throw new Error();
    }
  }

  createUserList(listName: string, listId: string): Observable<boolean> {
    return this.userListSdkService.createUserList(this.partnerService.partnerId, listName, listId);
  }

  startUserImport(numUsers: number, listId: string, updateUsers: boolean): void {
    const numUsersText = numUsers === 1 ? '1 user.' : numUsers + ' users.';
    this.bulkProcessor
      .initiateImportUsers(this.partnerService.partnerId, this.fileLocation, listId, updateUsers)
      .subscribe(() => this.snackbarService.successSnack("We've started to import " + numUsersText));
  }

  listUserLists(pageSize: number): Observable<UserList[]> {
    return this.userListSdkService.listUserLists(this.partnerService.partnerId, '', pageSize).pipe(
      expand((resp) => {
        if (resp?.pagingMetadata?.hasMore) {
          return this.userListSdkService.listUserLists(
            this.partnerService.partnerId,
            resp?.pagingMetadata?.nextCursor,
            pageSize,
          );
        } else {
          return EMPTY;
        }
      }),
      map((resp) => {
        if (!resp || !resp.userLists) {
          return [];
        }
        const userList: UserList[] = [];
        for (const r of resp.userLists) {
          userList.push({
            listId: r.listId,
            userListName: r.listName,
          });
        }
        return userList;
      }),
      reduce((acc, curr) => [...acc, ...curr], []),
    );
  }

  validateCSV(): Observable<string[]> {
    return this.bulkProcessor.validateImportUserFile(this.fileLocation, this.partnerService.partnerId);
  }
}

export interface UserList {
  listId: string;
  userListName: string;
}
